import React, { useMemo, useState, useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import clsx from 'clsx';

import { AutoNumeric } from '../../../../common/auto-numeric/AutoNumeric';
import { CommonProps } from './pricing-list/PricingItem';
import { useUserPermissions } from '../../../../../context/user/PermissionsProvider';
import { calculateValue } from '../../../../../utils/helper-functions';
import { PricingCalculator } from '../../../../../utils/PricingCalculator';
import { PoFormLabel, pricingConfig } from '../po-form-config';
import { FormPricingKeys } from './pricing-list/FormPricing';
import { useAutoPricingState } from './context/AutoPricingProvider';

import formStyles from '../PurchaseOrderForm.module.scss';
import { initialAutoPricingState } from '../../../../../constants/constants';

const { min } = pricingConfig[FormPricingKeys.TotalRtoPrice].validations;

export const totalRtoPriceTestId = 'TotalRtoPriceComponent';

export const TotalRtoPrice = (props: CommonProps) => {
  const { index, field, state } = props;
  const { hasAutoPricing } = useUserPermissions();
  const { autoPricings } = useAutoPricingState();
  const [value, setValue] = useState('');
  const [isDefaultFromPO, setIsDefaultFromPO] = useState(
    field.totalRtoPrice !== ''
  );
  const { totalCost: apiValue, weeklyRate, weeklyTerm } =
    autoPricings[state] || initialAutoPricingState;
  const fieldName = `pricing[${index}].totalRtoPrice`;

  const weeklyTermWatched = useWatch({ name: `pricing[${index}].weeklyTerm` });
  const weeklyRentWatched = useWatch({ name: `pricing[${index}].weeklyRent` });

  const totalRtoPrice = () =>
    !!weeklyTermWatched &&
    !!weeklyRentWatched &&
    calculateValue(
      PricingCalculator.totalRtoPrice,
      weeklyRentWatched,
      weeklyTermWatched
    );

  useEffect(() => {
    if (
      weeklyTerm === null &&
      weeklyRate === null &&
      apiValue === null &&
      !!weeklyTermWatched &&
      !!weeklyRentWatched &&
      !isDefaultFromPO
    ) {
      //* Value to be calculated from input change
      const totalPrice = totalRtoPrice();
      const cost =
        !!weeklyTermWatched &&
        !!weeklyRentWatched &&
        totalPrice &&
        totalPrice >= '1'
          ? totalPrice
          : '';
      setValue(cost);
    } else {
      setValue('');
      setIsDefaultFromPO(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyTermWatched, weeklyRentWatched]);

  const imperativeValueCheck =
    !!value ||
    !!apiValue ||
    weeklyRentWatched === '' ||
    weeklyTermWatched === '';

  const imperativeValue =
    value !== '' ? value : apiValue ? apiValue.toString() : '';

  return useMemo(
    () => (
      <div data-testid={totalRtoPriceTestId}>
        <label htmlFor={fieldName}>{PoFormLabel.TotalRTOPrice}:</label>
        <div className={clsx(formStyles.iconField, formStyles.noFillField)}>
          <span>$</span>

          <Controller
            as={AutoNumeric}
            name={fieldName}
            defaultValue={field.totalRtoPrice}
            {...(imperativeValueCheck && {
              imperativeValue: imperativeValue?.toString(),
              imperativeMode: true
            })}
            options={{
              minimumValue: 0,
              decimalPlaces: 2,
              ...(!hasAutoPricing && {
                maximumValue: Number.MAX_SAFE_INTEGER
              })
            }}
            placeholder="--"
            disabled
            rules={{ required: true, min }}
          />
        </div>
      </div>
    ),
    [
      fieldName,
      field.totalRtoPrice,
      imperativeValueCheck,
      imperativeValue,
      hasAutoPricing
    ]
  );
};
